import dicomParser from "dicom-parser";
import { DICOM_TAGS, MODALITY } from "../classes/enums/MedicalImagesEnum";
import { dbDateFormat } from "../classes/utils/DateUtils";
import { capitalizeFirstLetter } from "../classes/utils/ParsingUtils";

class DicomService {
  static async readMetadata(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = event => {
        const arrayBuffer = event.target.result;
        try {
          const byteArray = new Uint8Array(arrayBuffer);
          const dataSet = dicomParser.parseDicom(byteArray);

          const getString = tag => {
            const element = dataSet.elements[tag];
            return element ? dataSet.string(tag) : undefined;
          };

          const getInt = tag => {
            const str = getString(tag);
            return str !== undefined ? parseInt(str, 10) : undefined;
          };

          const studyDate = getString(DICOM_TAGS.STUDY_DATE);
          const seriesDate = getString(DICOM_TAGS.SERIES_DATE);
          const acquisitionDate = getString(DICOM_TAGS.ACQUISITION_DATE);

          const patientName = getString(DICOM_TAGS.PATIENT_NAME);
          const patientId = getString(DICOM_TAGS.PATIENT_ID);
          const patientBirthDate = getString(DICOM_TAGS.PATIENT_BIRTH_DATE);
          const patientSex = getString(DICOM_TAGS.PATIENT_SEX);

          let modality = getString(DICOM_TAGS.MODALITY);
          const imageOrientation = getString(
            DICOM_TAGS.IMAGE_ORIENTATION_PATIENT
          );
          const imagePosition = getString(DICOM_TAGS.IMAGE_POSITION_PATIENT);
          const patientPosition = getString(DICOM_TAGS.PATIENT_POSITION);
          const patientOrientation = getString(DICOM_TAGS.PATIENT_ORIENTATION);
          const seriesUID = getString(DICOM_TAGS.SERIES_INSTANCE_UID);
          const studyUID = getString(DICOM_TAGS.STUDY_INSTANCE_UID);
          const instanceUID = getString(DICOM_TAGS.SOP_INSTANCE_UID);
          const viewPosition = getString(DICOM_TAGS.VIEW_POSITION);
          const imageComments = getString(DICOM_TAGS.IMAGE_COMMENTS);

          const instanceNumber = getInt(DICOM_TAGS.INSTANCE_NUMBER);

          const seriesNumber = getInt(DICOM_TAGS.SERIES_NUMBER);

          // Validation fields
          const imageTypeRaw = getString(DICOM_TAGS.IMAGE_TYPE);
          // DICOM stores ImageType as backslash-separated values: "ORIGINAL\PRIMARY\AXIAL"
          const imageType = imageTypeRaw
            ? imageTypeRaw.split("\\").map(v => v.trim().toUpperCase())
            : [];

          // Pixel Data presence — element exists and has a non-zero length
          const pixelDataElement = dataSet.elements[DICOM_TAGS.PIXEL_DATA];
          const pixelDataPresent = !!(pixelDataElement && pixelDataElement.length > 0);

          // Pixel spacing: try (0028,0030) first, then (0018,1164)
          const pixelSpacingRaw = getString(DICOM_TAGS.PIXEL_SPACING);
          const imagerPixelSpacingRaw = getString(DICOM_TAGS.IMAGER_PIXEL_SPACING);

          const parseSpacing = raw => {
            if (!raw) return null;
            const parts = raw.split("\\").map(v => parseFloat(v));
            return parts.length >= 2 && parts.every(v => !isNaN(v)) ? parts : null;
          };

          const pixelSpacing = parseSpacing(pixelSpacingRaw);
          const imagerPixelSpacing = parseSpacing(imagerPixelSpacingRaw);

          const rescaleInterceptRaw = getString(DICOM_TAGS.RESCALE_INTERCEPT);
          const rescaleSlopeRaw = getString(DICOM_TAGS.RESCALE_SLOPE);
          const rescaleIntercept = rescaleInterceptRaw !== undefined ? parseFloat(rescaleInterceptRaw) : undefined;
          const rescaleSlope = rescaleSlopeRaw !== undefined ? parseFloat(rescaleSlopeRaw) : undefined;

          const manufacturer = getString(DICOM_TAGS.MANUFACTURER);
          const studyDescription = getString(DICOM_TAGS.STUDY_DESCRIPTION);
          const seriesDescription = getString(DICOM_TAGS.SERIES_DESCRIPTION);

          let firstName = "";
          let lastName = "";
          if (patientName) {
            const nameParts = patientName.split("^");
            if (nameParts.length >= 2) {
              lastName = capitalizeFirstLetter(nameParts[0]) || "";
              firstName = capitalizeFirstLetter(nameParts[1]) || "";
            } else {
              lastName = capitalizeFirstLetter(patientName);
            }
          }

          let gender = null;
          if (patientSex) {
            if (patientSex.toUpperCase() === "M") {
              gender = "male";
            } else if (patientSex.toUpperCase() === "F") {
              gender = "female";
            } else {
              gender = "other";
            }
          }

          let orientation = this.formatOrientation(modality, {
            imageOrientation,
            patientOrientation,
          });

          const creationDate = acquisitionDate || seriesDate || studyDate;
          const formattedDate = creationDate
            ? dbDateFormat(creationDate)
            : null;

          resolve({
            creationDate: formattedDate,
            modality,
            orientation,
            imagePosition,
            seriesId: seriesUID,
            studyId: studyUID,
            instanceId: instanceUID,
            instanceNumber,
            seriesNumber,

            patientInfo: {
              firstName,
              lastName,
              patientId,
              birthDate: patientBirthDate,
              gender,
            },
            imageType,
            pixelDataPresent,
            pixelSpacing,
            imagerPixelSpacing,
            rescaleIntercept,
            rescaleSlope,
          });
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = error => reject(error);
      reader.readAsArrayBuffer(file);
    });
  }

  static formatOrientation(modality, data) {
    const { imageOrientation, patientOrientation } = data;
    let orientation = null;

    if (modality === MODALITY.CT || modality === MODALITY.MR) {
      if (imageOrientation) {
        const values = imageOrientation.split("\\").map(Number);

        if (values.length !== 6) {
          return null;
        }

        const orientationRow = [values[0], values[1], values[2]];
        const orientationCol = [values[3], values[4], values[5]];

        orientation = `[[${orientationRow.join(",")}],[${orientationCol.join(
          ","
        )}]]`;
      }
    }

    if (
      modality === MODALITY.DX ||
      modality === MODALITY.CR ||
      modality === MODALITY.MO
    ) {
      if (patientOrientation) {
        const orientationParts = patientOrientation.split("\\");
        if (orientationParts.length === 2) {
          orientation = `[[${orientationParts[0]}],[${orientationParts[1]}]]`;
        }
      }
    }

    return orientation;
  }
}

export default DicomService;
